import { useForceUpdate } from "@src/hooks/useForceUpdate";
import _ from "lodash";
import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ResizableBox } from "react-resizable";
import { GRID_WIDTHS_RESET_EVENT, readStoredColumnWidths, writeStoredColumnWidth } from "@src/utils/gridPreferences.utils";
import { TableVirtuoso, TableVirtuosoHandle } from "react-virtuoso";
import { clx } from "../utils/stringUtils";
import { isRowClickSelectable } from "./rowClickSelection";
import { CellRenderer } from "./CellRenderer";
import { MyTooltip } from "../tooltip/Tooltip";
import { HelpCircle } from "lucide-react";
import Checkbox from "./Checkbox";
import styles from "./VGrid.module.scss";
import { getElementCoords } from "@src/utils/general.utils";
import { SortAscIcon } from "@src/assets/general-icons/SortAscIcon";

export const ENTER_EDIT_EVENT = "vgrid-enter-edit";

const readStoredSort = (tableId?: string): Array<ISort> => {
	if (!tableId) return [];
	const storedSort = localStorage.getItem(`${tableId}.sortOrder`);
	if (!storedSort) return [];
	try {
		const parsedSort = JSON.parse(storedSort);
		return Array.isArray(parsedSort) ? (parsedSort as Array<ISort>) : [];
	} catch (e) {
		console.warn(`Failed to parse sort order for table "${tableId}":`, e);
		return [];
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UNKNOWN = any;
export type TExtend = Record<string, UNKNOWN>;
export interface IValueGetterParams<T extends TExtend> {
	rowData: T;
	column: IColumn<T>;
}
export interface IHeaderRendererParams<T extends TExtend> {
	column: IColumn<T>;
}
export interface IParams<T extends TExtend, K> {
	rowData: T;
	value: K;
	column: IColumn<T>;
}

export type IColumn<T extends TExtend> = {
	[K in keyof T]: {
		id: string;
		accessor?: K;
		header?: string;
		headerTooltip?: React.ReactNode;
		width: number;
		valueGetter?: (params: IValueGetterParams<T>) => UNKNOWN;
		formatValue?: (value: T[K], params: IParams<T, T[K]>) => JSX.Element | string | number;
		formatTooltip?: (value: T[K], params: IParams<T, T[K]>) => JSX.Element | string | number;
		headerRenderer?: (params: IHeaderRendererParams<T>) => JSX.Element | string | number;
		cellRenderer?: (params: IParams<T, T[K]>) => JSX.Element | string | number;
		getClassName?: (params: IParams<T, T[K]>) => string;
		getStyle?: (params: IParams<T, T[K]>) => CSSProperties;
		getHeaderStyle?: (params: IHeaderRendererParams<T>) => CSSProperties;
		getCellStyle?: (params: IParams<T, T[K]>) => CSSProperties;
		isSticky?: boolean;
		isEditable?: boolean | ((params: IParams<T, T[K]>) => boolean);
		editableLabel?: string;
		onEditClick?: (params: IParams<T, T[K]>) => void;
		isDisabled?: (rowData: T) => boolean;
		checkboxInputStyle?: (rowData: T) => CSSProperties;
		minWidth?: number;
		maxWidth?: number;
		fullWidth?: boolean;
		hasTooltip?: boolean;
		disableTitle?: boolean;
		sortable?: boolean;
		isCurrency?: boolean;
		editInputSize?: "n" | "sm" | "xsm";
		editBtnClassName?: string;
		setTofixed?: number;
	};
}[keyof T];

export interface IRowDataValueChangeProps<T extends TExtend> {
	accesor: keyof T;
	originalValue: UNKNOWN;
	value: UNKNOWN;
	columnId: string;
	rowId: string | number;
	rowData: T;
}
type IVGridProps<T extends TExtend> = {
	[K in keyof T]: {
		columns: Array<IColumn<T>>;
		rowsData: Array<T>;
		theme?: "primary" | "secondary";
		bottomContainer?: JSX.Element;
		rowSelection?: "none" | "single" | "multiple";
		wrapperStyle?: CSSProperties;
		selectedRowId?: string | number;
		initialPageIndex?: number;
		onSelectionChange?: (rows: Array<T>) => void;
		getRowClassName?: (params: { rowData: T }) => string;
		getTableHeaderClassName?: () => string;
		getRowStyle?: (params: IParams<T, T[K]>) => string;
		onSortChange?: (sort: Array<ISort>) => void;
		onPageIndexChange?: (pageIndex: number) => void;
		getRowNodeId: (row: T) => string | number;
		getCheckboxIsDisabled?: (rowData: T) => boolean;
		getCheckboxInputStyle?: (rowData: T) => CSSProperties;
		rowClickSelectionIgnoredColumns?: Array<string>;
		onRowDataValueChange?: (params: IRowDataValueChangeProps<T>) => void;
		onChangeMapChange?: (changesMap: Map<string, IRowDataValueChangeProps<T>>) => void;
		onRowSelection?: (data: T) => void;
		hasSelectedRowBorder?: boolean;
		changesMapVersion?: number;
		isSaving?: boolean;
		disableBorder?: boolean;
		tableId?: string;
		singleFieldSorting?: boolean;
		getSortGroup?: (row: T) => number;
		getContextMenuInfo?: ({ rowData, x, y }: { rowData: T; x: number | null; y: number | null }) => void;
		enableEnterAdvance?: boolean;
		initialChangesMap?: Map<string, IRowDataValueChangeProps<T>>;
	};
}[keyof T];

interface ISort {
	columnId: string;
	direction: "asc" | "desc";
}
interface IState<T extends TExtend> {
	sort: Array<ISort>;
	selectedRowId?: string | number;
	columnSizes: Map<string, number>;
	changesMap: Map<string, IRowDataValueChangeProps<T>>;
}

export interface VGridRef {
	setCheckedRowIds: (rows: Array<{ id: string | number; isChecked: boolean }>) => void;
	strictRowSet: (rows: Array<{ id: string | number; isChecked: boolean }>) => void;
	clearSelection: () => void;
}

export const VGridInner = <T extends TExtend>(
	{
		columns,
		rowsData,
		theme = "primary",
		bottomContainer,
		rowSelection = "none",
		wrapperStyle,
		onSelectionChange,
		getRowClassName,
		getTableHeaderClassName,
		getRowNodeId,
		getCheckboxIsDisabled,
		getCheckboxInputStyle,
		rowClickSelectionIgnoredColumns,
		onSortChange,
		onRowDataValueChange,
		onChangeMapChange,
		onRowSelection,
		getContextMenuInfo,
		selectedRowId,
		changesMapVersion = 0,
		isSaving = false,
		disableBorder = false,
		hasSelectedRowBorder = false,
		tableId,
		singleFieldSorting = true,
		getSortGroup,
		enableEnterAdvance = false,
		initialChangesMap,
	}: IVGridProps<T>,
	ref: React.Ref<VGridRef>,
) => {
	const [staticState] = useState({
		checkedRowIds: new Set<string | number>(),
	});

	const forceUpdate = useForceUpdate();

	const [state, setState] = useState<IState<T>>(() => ({
		sort: readStoredSort(tableId),
		selectedRowId: selectedRowId,
		columnSizes: new Map<string, number>(Object.entries(readStoredColumnWidths(tableId ?? ""))),
		changesMap: initialChangesMap ? new Map(initialChangesMap) : new Map<string, IRowDataValueChangeProps<T>>(),
	}));

	useEffect(() => {
		if (!tableId) return;

		const handleWidthsReset = (event: Event) => {
			if ((event as CustomEvent<{ tableId: string }>).detail?.tableId !== tableId) return;
			setState((x) => ({ ...x, columnSizes: new Map<string, number>() }));
		};

		window.addEventListener(GRID_WIDTHS_RESET_EVENT, handleWidthsReset);
		return () => window.removeEventListener(GRID_WIDTHS_RESET_EVENT, handleWidthsReset);
	}, [tableId]);

	const lastCheckedRowIndex = useRef<number | null>(null);
	const virtuosoRef = useRef<TableVirtuosoHandle>(null);
	const scrolledToRowIdRef = useRef<string | number | null>(null);
	const tableRef = useRef<HTMLTableElement>(null);

	useEffect(() => {
		if (state.changesMap.size > 0) onChangeMapChange?.(state.changesMap);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!tableId) return;

		const storedSort = readStoredSort(tableId);
		if (!storedSort.length) return;
		setState((prev) => ({ ...prev, sort: storedSort }));
		onSortChange?.(storedSort);
	}, [tableId, onSortChange]);

	const applyRowCheckedChange = (rowData: T, isChecked: boolean, isRangeSelect: boolean) => {
		const id = getRowNodeId(rowData);
		const currentIndex = sortedRowsData.findIndex((row) => getRowNodeId(row) === id);

		if (isRangeSelect && lastCheckedRowIndex.current !== null) {
			const start = Math.min(lastCheckedRowIndex.current, currentIndex);
			const end = Math.max(lastCheckedRowIndex.current, currentIndex);

			for (let i = start; i <= end; i++) {
				const row = sortedRowsData[i];
				const rowId = getRowNodeId(row);
				if (getCheckboxIsDisabled?.(row)) continue;
				if (isChecked) staticState.checkedRowIds.add(rowId);
				else staticState.checkedRowIds.delete(rowId);
			}
		} else {
			if (isChecked) staticState.checkedRowIds.add(id);
			else staticState.checkedRowIds.delete(id);
			lastCheckedRowIndex.current = currentIndex;
		}

		_onSelectionChange();
	};

	const _onSelectionChange = () => {
		forceUpdate();
		onSelectionChange?.(sortedRowsData.filter((x) => staticState.checkedRowIds.has(getRowNodeId(x))));
	};

	useImperativeHandle(ref, () => ({
		setCheckedRowIds: (rows) => {
			rows.forEach((row) => {
				if (row.isChecked) staticState.checkedRowIds.add(row.id);
				else staticState.checkedRowIds.delete(row.id);
			});
			_onSelectionChange();
		},
		strictRowSet: (rows) => {
			const row = rows[0];
			staticState.checkedRowIds = new Set();
			if (row.isChecked) staticState.checkedRowIds.add(row.id);
			_onSelectionChange();
		},
		clearSelection: () => {
			if (!staticState.checkedRowIds.size) return;
			staticState.checkedRowIds.clear();
			_onSelectionChange();
		},
	}));

	useEffect(() => {
		if (!selectedRowId) return;
		setState((x) => ({ ...x, selectedRowId: selectedRowId }));
	}, [selectedRowId, state.selectedRowId]);

	useEffect(() => {
		if (!changesMapVersion) return;
		setState((x) => ({ ...x, changesMap: new Map() }));
	}, [changesMapVersion]);

	const convertTo24Hour = (time: string) => {
		if (!time || typeof time !== "string") return "00:00:00";

		const parts = time.trim().split(" ");
		if (parts.length !== 2) return "00:00:00";

		const [hourMin, modifierRaw] = parts;
		const modifier = modifierRaw?.toLowerCase?.();
		if (!modifier) return "00:00:00";

		const [h, m] = hourMin.split(":").map(Number);
		let hours = h;
		const minutes = m;
		if (isNaN(hours) || isNaN(minutes)) return "00:00:00";

		if (modifier === "pm" && hours < 12) hours += 12;
		if (modifier === "am" && hours === 12) hours = 0;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
	};

	const handleParentCheckboxChange = (isChecked: boolean) => {
		if (isChecked) {
			staticState.checkedRowIds.clear();
			rowsData.forEach((x) => {
				const isDisabled = !!getCheckboxIsDisabled?.(x);
				if (isDisabled) return;
				staticState.checkedRowIds.add(getRowNodeId(x));
			});
		} else {
			staticState.checkedRowIds.clear();
		}
		_onSelectionChange();
	};

	const customColumns = (() => {
		let newColumns = columns.map<IColumn<T> & { width?: number }>((column) => {
			return {
				...column,
				width: state.columnSizes.get(column.id) ?? column.width ?? column.minWidth,
				minWidth: column?.minWidth,
			};
		});

		if (rowSelection == "multiple") {
			newColumns.unshift({
				id: "__checkbox_column",
				width: 30,
				headerRenderer: () => {
					const enabledRows = rowsData.filter((x) => !getCheckboxIsDisabled?.(x));
					const isChecked =
						enabledRows.length > 0 && enabledRows.every((x) => staticState.checkedRowIds.has(getRowNodeId(x)));
					return (
						<div className={clx(styles.headContainer, styles.headCheckboxContainer)}>
							<div className={styles.headCheckbox}>
								<Checkbox
									controlSize={theme == "primary" ? "m" : "sm"}
									isChecked={isChecked}
									onChange={handleParentCheckboxChange}
								/>
							</div>
						</div>
					);
				},
				isDisabled: getCheckboxIsDisabled,
				checkboxInputStyle: getCheckboxInputStyle,
				formatTooltip: () => "",
				getClassName: () => styles.checkboxColumn,
				formatValue: (_value, params) => {
					return (
						<Checkbox
							controlSize={theme === "primary" ? "m" : "sm"}
							inputStyle={params.column.checkboxInputStyle?.(params.rowData)}
							isDisabled={params.column.isDisabled?.(params.rowData)}
							isChecked={staticState.checkedRowIds.has(getRowNodeId(params.rowData))}
							onChange={(isChecked, event) => applyRowCheckedChange(params.rowData, isChecked, !!event?.shiftKey)}
						/>
					);
				},
			});
		}

		// Maybe add this later
		// if (!_.find(newColumns, (x) => !!x.fullWidth)) {
		// 	newColumns.push({
		// 		id: "__last_column",
		// 		width: 0,
		// 		fullWidth: true,
		// 		headerRenderer: () => "",
		// 	});
		// }

		const nonStickyColumns = newColumns.filter((column) => !column.isSticky);
		const stickyColumns = newColumns.filter((column) => column.isSticky);

		newColumns = [...nonStickyColumns, ...stickyColumns];
		return newColumns;
	})();

	const sortedRowsData = (() => {
		if (state.sort.length === 0 && !getSortGroup) return rowsData;

		return [...rowsData].sort((a, b) => {
			if (getSortGroup) {
				const groupDiff = getSortGroup(a) - getSortGroup(b);
				if (groupDiff !== 0) return groupDiff;
			}
			for (const { columnId, direction } of state.sort) {
				const column = customColumns.find((x) => x.id === columnId);
				if (!column) continue;

				let valueA = column.valueGetter?.({ rowData: a, column }) ?? a[column.accessor as keyof T];
				let valueB = column.valueGetter?.({ rowData: b, column }) ?? b[column.accessor as keyof T];

				if (typeof valueA === "string" && typeof valueB === "string" && column.id.toLowerCase().includes("time")) {
					const isInvalid = (v: string) =>
						!v || v.toLowerCase() === "tba" || isNaN(Date.parse(`1970-01-01T${convertTo24Hour(v)}`));

					const toDate = (timeStr: string) => new Date(`1970-01-01T${convertTo24Hour(timeStr)}`);

					const aInvalid = isInvalid(valueA);
					const bInvalid = isInvalid(valueB);

					if (aInvalid && bInvalid) continue;
					if (aInvalid) return 1;
					if (bInvalid) return -1;

					valueA = toDate(valueA);
					valueB = toDate(valueB);
				}
				if (valueA === valueB) continue;
				const comparison = valueA > valueB ? 1 : -1;
				return direction === "asc" ? comparison : -comparison;
			}
			return 0;
		});
	})();

	const initialScrollIndex = useMemo(() => {
		if (selectedRowId == null) return 0;
		const index = sortedRowsData.findIndex((row) => getRowNodeId(row) === selectedRowId);
		return index < 0 ? 0 : { index, align: "center" as const };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedRowId == null) {
			scrolledToRowIdRef.current = null;
			return;
		}
		if (scrolledToRowIdRef.current === selectedRowId) return;

		const index = sortedRowsData.findIndex((row) => getRowNodeId(row) === selectedRowId);
		if (index < 0) return;

		scrolledToRowIdRef.current = selectedRowId;
		const frame = requestAnimationFrame(() => virtuosoRef.current?.scrollIntoView({ index, align: "center" }));
		return () => cancelAnimationFrame(frame);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRowId, sortedRowsData]);

	const isCellEditable = (column: (IColumn<T> & { width?: number }) | undefined, row: T): boolean => {
		if (!column) return false;
		if (typeof column.isEditable === "function") {
			const value = column.valueGetter?.({ rowData: row, column }) ?? (column.accessor ? row[column.accessor] : undefined);
			return column.isEditable({ rowData: row, value, column });
		}
		return !!column.isEditable;
	};

	const focusEditableCell = (index: number, columnId: string, attempt = 0) => {
		const cell = tableRef.current?.querySelector<HTMLElement>(`tr[data-index="${index}"] td[data-col-id="${columnId}"]`);
		if (cell) {
			cell.dispatchEvent(new CustomEvent(ENTER_EDIT_EVENT));
			if (attempt < 4) requestAnimationFrame(() => focusEditableCell(index, columnId, attempt + 1));
			return;
		}
		if (attempt < 20) requestAnimationFrame(() => focusEditableCell(index, columnId, attempt + 1));
	};

	const handleAdvance = ({ rowId, columnId }: { rowId: string | number; columnId: string }) => {
		const currentIndex = sortedRowsData.findIndex((row) => getRowNodeId(row) === rowId);
		if (currentIndex < 0) return;
		const column = customColumns.find((c) => c.id === columnId);
		let nextIndex = currentIndex + 1;
		while (nextIndex < sortedRowsData.length && !isCellEditable(column, sortedRowsData[nextIndex])) {
			nextIndex++;
		}
		if (nextIndex >= sortedRowsData.length) return;
		virtuosoRef.current?.scrollIntoView({ index: nextIndex });
		requestAnimationFrame(() => focusEditableCell(nextIndex, columnId));
	};

	const localOnRowDataValueChange = (params: IRowDataValueChangeProps<T>) => {
		setState((x) => {
			const newChangesMap = new Map(x.changesMap);
			const changeMapId = `row:${params.rowId}-col:${params.columnId}`;
			if (params.value == params.originalValue) {
				newChangesMap.delete(changeMapId);
			} else {
				newChangesMap.set(changeMapId, params);
			}
			onChangeMapChange?.(newChangesMap);
			onRowDataValueChange?.(params);
			return {
				...x,
				changesMap: newChangesMap,
			};
		});
	};

	// useEffect(() => {
	// 	const handleResize = () => {
	// 		const tableEl = tableRef.current;
	// 		if (!tableEl) return;
	// 		const filterCustomColumns = customColumns.filter((x) => !x.fullWidth && !x.isSticky && x.id != "__checkbox_column");
	// 		let totalWidth = _.sumBy(filterCustomColumns, (x) => x.width);
	// 		const newWidths = new Map<
	// 			string,
	// 			{
	// 				percentage: number;
	// 				width: number;
	// 			}
	// 		>();
	// 		filterCustomColumns.forEach((column) => {
	// 			newWidths.set(column.id, {
	// 				percentage: column.width / totalWidth,
	// 				width: column.width,
	// 			});
	// 		});
	// 		let fullWidthColumnWidth = 0;
	// 		tableEl.querySelectorAll("thead tr td.fullWidth").forEach((td) => {
	// 			fullWidthColumnWidth = td.clientWidth > 16 ? td.clientWidth : fullWidthColumnWidth;
	// 		});
	// 		filterCustomColumns.forEach((column) => {
	// 			const newWidth = newWidths.get(column.id);
	// 			if (!newWidth) return;
	// 			newWidth.width = newWidth.width - 10 + fullWidthColumnWidth * newWidth.percentage;
	// 			newWidths.set(column.id, newWidth);
	// 		});
	// 		setState((x) => {
	// 			const newColumnSizes = new Map(x.columnSizes);
	// 			newWidths.forEach((value, key) => {
	// 				newColumnSizes.set(key, value.width);
	// 			});
	// 			return {
	// 				...x,
	// 				columnSizes: newColumnSizes,
	// 			};
	// 		});
	// 	};

	// 	window.addEventListener("resize", _.debounce(handleResize, 100));
	// 	return () => {
	// 		window.removeEventListener("resize", handleResize);
	// 	};
	// }, [tableRef.current]);

	const handleTDClickGroupSorting = (column: IColumn<T> & { width?: number }) => {
		if (!column.sortable) return;

		setState((prev) => {
			const sortIndex = prev.sort.findIndex((s) => s.columnId === column.id);
			const newSort = [...prev.sort];

			if (sortIndex === -1) {
				// Not in sort → add as ascending
				newSort.push({ columnId: column.id, direction: "asc" });
			} else if (newSort[sortIndex].direction === "asc") {
				// Ascending → switch to descending
				newSort[sortIndex].direction = "desc";
			} else {
				// Descending → remove from sort
				newSort.splice(sortIndex, 1);
			}

			if (tableId) {
				const key = `${tableId}.sortOrder`;
				newSort.length > 0 ? localStorage.setItem(key, JSON.stringify(newSort)) : localStorage.removeItem(key);
			}

			onSortChange?.(newSort);
			return { ...prev, sort: newSort };
		});
	};

	const handleTDClickSingleSorting = (column: IColumn<T> & { width?: number }) => {
		if (!column.sortable) return;

		setState((prev) => {
			const existing = prev.sort.find((s) => s.columnId === column.id);

			// single-sort cycle: none -> asc -> desc -> none
			let newSort: Array<ISort> = [];
			if (!existing) newSort = [{ columnId: column.id, direction: "asc" }];
			else if (existing.direction === "asc") newSort = [{ columnId: column.id, direction: "desc" }];
			else newSort = [];

			if (tableId) {
				const key = `${tableId}.sortOrder`;
				newSort.length > 0 ? localStorage.setItem(key, JSON.stringify(newSort)) : localStorage.removeItem(key);
			}

			onSortChange?.(newSort);
			return { ...prev, sort: newSort };
		});
	};

	return (
		<div className={styles.tableWrapper} style={wrapperStyle}>
			<div className={clx(styles.tableContainer, styles[`${theme}Theme`])}>
				<TableVirtuoso
					ref={virtuosoRef}
					initialTopMostItemIndex={initialScrollIndex}
					totalCount={sortedRowsData.length}
					components={{
						Table: ({ style, ...props }) => <table ref={tableRef} className={styles.table} {...props} style={style} />,
						TableBody: React.forwardRef(({ style, ...props }, ref) => (
							<tbody style={{ position: "relative", ...style }} {...props} ref={ref} />
						)),
						TableRow: (props) => {
							const index = props["data-index"];
							const rowData = sortedRowsData[index];
							const isSelected = state.selectedRowId == getRowNodeId(rowData);
							let isSelectedMultiple = false;

							if (rowSelection === "multiple") {
								isSelectedMultiple = staticState.checkedRowIds.has(getRowNodeId(rowData));
							}

							return (
								<tr
									data-selected-has-border={hasSelectedRowBorder && isSelected}
									className={clx({
										[styles.tbody_tr]: true,
										[styles.selected]: isSelected,
										[styles.selectedMultiple]: isSelectedMultiple,
										[getRowClassName?.({ rowData }) ?? ""]: true,
									})}
									{...props}
									onClick={(event) => {
										const id = getRowNodeId(rowData);

										if (rowSelection === "multiple" && onSelectionChange && !onRowSelection) {
											if (getCheckboxIsDisabled?.(rowData)) return;
											if (!event.currentTarget.contains(event.target as Node)) return;

											const isRangeSelect = event.shiftKey;
											const canSelect = isRowClickSelectable(event.target as HTMLElement, {
												ignoredColumns: rowClickSelectionIgnoredColumns,
												isEditableColumn: (columnId) =>
													isCellEditable(
														customColumns.find((column) => String(column.id) === columnId),
														rowData,
													),
												getSelectedText: isRangeSelect ? undefined : () => window.getSelection()?.toString() ?? "",
											});
											if (!canSelect) return;

											if (isRangeSelect) window.getSelection()?.removeAllRanges();

											applyRowCheckedChange(rowData, !staticState.checkedRowIds.has(id), isRangeSelect);
											return;
										}

										if (state.selectedRowId == id) return;
										if (onRowSelection) {
											setState((x) => ({ ...x, selectedRowId: id }));
											onRowSelection(rowData);
											return;
										}
										if (!onSelectionChange || rowSelection != "single") return;
										setState((x) => ({ ...x, selectedRowId: id }));
										onSelectionChange([rowData]);
									}}
									onContextMenu={(e) => {
										if (!getContextMenuInfo) return;
										e.preventDefault();
										const { x, y } = getElementCoords(e);
										getContextMenuInfo({ rowData, x, y });
									}}
								/>
							);
						},
					}}
					fixedHeaderContent={() => {
						return (
							<tr
								className={clx({
									[styles.thead_tr]: true,
									[getTableHeaderClassName?.() ?? ""]: true,
								})}>
								{customColumns.map((column) => {
									return (
										<td
											key={column.id}
											onClick={() =>
												singleFieldSorting ? handleTDClickSingleSorting(column) : handleTDClickGroupSorting(column)
											}
											className={clx({ fullWidth: !!column.fullWidth, [styles.thead_td]: true })}
											style={{
												width: column.width,
												...(column.isSticky
													? {
															position: "sticky",
															right: "0px",
														}
													: {}),
												...(column.fullWidth ? { width: "100%" } : {}),
												...(column.getHeaderStyle?.({ column }) ?? {}),
											}}>
											{column.id != "__checkbox_column" && column.id != "__last_column" && (
												<ResizableBox
													width={column.fullWidth ? Infinity : column.width}
													height={0}
													axis="x"
													resizeHandles={["e"]}
													onResizeStop={(e, data) => {
														writeStoredColumnWidth(tableId ?? "", column.id, data.size.width);
														setState((x) => {
															return {
																...x,
																columnSizes: x.columnSizes.set(column.id, data.size.width),
															};
														});
													}}
													minConstraints={[column.minWidth ?? 50, 0]}
													maxConstraints={[column.maxWidth ?? Number.MAX_SAFE_INTEGER, 0]}
													handle={<div className={styles.resizeHandle} />}
													style={{
														backgroundColor: "Red",
														height: "0px",
														position: "absolute",
														top: 0,
														display: "flex",
														justifyContent: "end",
														width: "100%",
													}}></ResizableBox>
											)}

											{column.headerRenderer?.({
												column: column,
											}) ?? (
												<div
													className={styles.headContainer}
													style={{
														width: column.width,
														...(column.fullWidth ? { width: "100%" } : {}),
													}}>
													<div className={styles.headContent}>
														<span className={styles.headLabel}>{column.header}</span>
														{column.headerTooltip && (
															<MyTooltip
																id={`vgrid-head-${column.id}`}
																place="top"
																popup={column.headerTooltip}
																element={
																	<span className={styles.headTooltipIcon} aria-label="Column info">
																		<HelpCircle size={14} />
																	</span>
																}
															/>
														)}
													</div>
													{state.sort.find((s) => s.columnId === column.id) && (
														<div className={styles.headSort}>
															<SortAscIcon
																isDesc={state.sort.find((s) => s.columnId === column.id)?.direction === "desc"}
															/>
														</div>
													)}
												</div>
											)}
										</td>
									);
								})}
							</tr>
						);
					}}
					itemContent={(index) => {
						const rowData = sortedRowsData[index];
						return customColumns.map((column) => {
							const cellProps = {
								key: column.id,
								theme,
								column,
								rowData,
								rowId: getRowNodeId(rowData),
								changesMap: state.changesMap,
								isSaving,
								disableBorder,
								onRowDataValueChange: localOnRowDataValueChange,
								highlightIfOverThreshold: true,
								onAdvance: enableEnterAdvance ? handleAdvance : undefined,
							};
							return <CellRenderer<T> {...cellProps} />;
						});
					}}
				/>
			</div>
			{bottomContainer && (
				<div className={styles.bottomContainer}>
					<div className={styles.pageInfo}>{bottomContainer}</div>
				</div>
			)}
		</div>
	);
};

export const VGrid = forwardRef(VGridInner) as <T extends TExtend>(
	props: IVGridProps<T> & { ref?: React.Ref<VGridRef> },
) => ReturnType<typeof VGridInner>;
